import { Injectable, computed, inject, signal } from '@angular/core';
import { StoresApiService } from '../../../core/stores/stores-api.service';
import { StoreOptionMapper } from './store-option.mapper';
import { StoreOption } from './store-option';
import { PosOfflineDbService } from '../offline/pos-offline-db.service';
import { OfflineService } from '../offline/offline.service';
import { StoreType } from '../../../core/stores/enums/store-type.enum';

@Injectable({
  providedIn: 'root',
})
export class StoreSelectorService {
  #storesService = inject(StoresApiService);
  #offlineDbService = inject(PosOfflineDbService);
  #offlineService = inject(OfflineService);

  #stores = signal<StoreOption[] | null>(null);
  #initializationPromise: Promise<void> | null = null;

  stores = this.#stores.asReadonly();
  loading = signal(false);

  // Computed signal for F&B stores only (uses store type from stores list)
  fnbStores = computed(() => {
    const stores = this.stores();
    if (!stores) return [];
    return stores.filter((store) => store.type === StoreType.FNB);
  });
  hasFnbStore = computed(() => this.fnbStores().length > 0);

  // Computed signal for retail stores only
  retailStores = computed(() => {
    const stores = this.stores();
    if (!stores) return [];
    return stores.filter((store) => store.type === StoreType.RETAIL);
  });
  hasRetailStore = computed(() => this.retailStores().length > 0);

  empty = computed(() => {
    const stores = this.stores();
    return !stores || stores.length === 0;
  });
  defaultSelectedId = computed(() => {
    const stores = this.stores();
    return stores && stores.length > 0 ? stores[0].id : null;
  });

  async init(): Promise<void> {
    if (this.#initializationPromise) {
      await this.#initializationPromise;
      return;
    }

    if (this.#stores() !== null) return;

    this.#initializationPromise = this.loadStores();
    try {
      await this.#initializationPromise;
    } finally {
      this.#initializationPromise = null;
    }
  }

  async loadStores() {
    try {
      this.loading.set(true);

      if (this.#offlineService.isOffline()) {
        const cachedStores = await this.#offlineDbService.getStoreList();
        if (cachedStores && cachedStores.length > 0) {
          this.#stores.set(StoreOptionMapper.toModel(cachedStores));
        } else {
          console.warn('[Store Selector] No cached stores found');
          this.#stores.set([]);
        }
        this.loading.set(false);
        return;
      }

      const stores = await this.#storesService.findStoreNames();
      const storesWithOptions = StoreOptionMapper.toModel(stores);

      this.#stores.set(storesWithOptions);

      await this.#offlineDbService.saveStoreList(stores);
    } catch (error) {
      console.error('[Store Selector] Error loading stores:', error);

      const cachedStores = await this.#offlineDbService.getStoreList();
      if (cachedStores && cachedStores.length > 0) {
        this.#stores.set(StoreOptionMapper.toModel(cachedStores));
      } else {
        this.#stores.set([]);
      }
    } finally {
      this.loading.set(false);
    }
  }

  reset(): void {
    this.#stores.set(null);
  }
}
